import { Badge, Button, Card, EmptyState, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiRequestError } from "../../lib/apiClient";

type Oturum = {
  id: string;
  current: boolean;
  device: string;
  browser: string | null;
  os: string | null;
  ip: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
};

/**
 * AKTİF CİHAZLAR (CLOS-520).
 *
 * ⚠ Bu bölüm yoktu: kullanıcı hangi cihazlarda oturumu olduğunu göremiyor ve şüpheli bir
 * oturumu sonlandıramıyordu. Uçlar hazırdı, onlara giden yol yoktu.
 *
 * ⚠ MEVCUT oturum tek tek sonlandırılamıyor. Kendini oturumdan atmak isteyen kullanıcı
 * çıkış yapar; buradaki düğme "şüpheli cihazı kes" içindir ve yanlışlıkla kendi oturumunu
 * kesen kullanıcı ne olduğunu anlamazdı.
 *
 * ⚠ IP maskeli geliyor (sunucu kararı); ekran onu olduğu gibi gösteriyor — burada
 * yeniden biçimlendirmek, sunucunun gizleme kararını sessizce yorumlamak olurdu.
 */
export function DevicesSection() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const sorgu = useQuery({
    queryKey: ["oturum", "cihazlar"],
    queryFn: async () => (await api<Oturum[]>("/api/session/devices")).data,
  });

  const tazele = async () => {
    await queryClient.invalidateQueries({ queryKey: ["oturum", "cihazlar"] });
  };

  const sonlandir = useMutation({
    mutationFn: async (id: string) =>
      (await api(`/api/session/devices/${id}`, { method: "DELETE" })).data,
    onSuccess: async () => {
      await tazele();
      toast.success("Oturum sonlandırıldı");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Oturum sonlandırılamadı");
    },
  });

  const digerleriniKapat = useMutation({
    mutationFn: async () =>
      (await api("/api/session/devices/revoke-others", { method: "POST" })).data,
    onSuccess: async () => {
      await tazele();
      toast.success("Diğer oturumlar kapatıldı");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Oturumlar kapatılamadı");
    },
  });

  if (sorgu.isPending) {
    return (
      <Card title="Aktif Cihazlar">
        <div role="status" aria-label="Cihazlar yükleniyor">
          <Skeleton lines={3} />
        </div>
      </Card>
    );
  }

  const oturumlar = sorgu.data ?? [];
  const digerVar = oturumlar.some((o) => !o.current);

  return (
    <Card
      title="Aktif Cihazlar"
      actions={
        /* Yapacak bir şeyi olmayan düğme sunulmuyor: tek oturumda toplu kapatma anlamsız. */
        digerVar ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={digerleriniKapat.isPending}
            onClick={() => digerleriniKapat.mutate()}
          >
            Diğer oturumları kapat
          </Button>
        ) : null
      }
    >
      {oturumlar.length === 0 ? (
        <EmptyState title="Aktif oturum bulunmuyor." />
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {oturumlar.map((o) => (
            <li key={o.id} className="flex flex-wrap items-center gap-3 py-2">
              <div className="mr-auto">
                <p className="text-sm text-fg-strong">{o.device}</p>
                <p className="text-xs text-fg-soft">
                  {[o.os, o.browser, o.ip].filter(Boolean).join(" · ")}
                </p>
                <p className="text-xs text-fg-muted">
                  Son görülme: {new Date(o.lastSeenAt).toLocaleString("tr-TR")}
                </p>
              </div>

              {o.current ? (
                <Badge tone="success">Bu cihaz</Badge>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  loading={sonlandir.isPending}
                  onClick={() => sonlandir.mutate(o.id)}
                >
                  Sonlandır
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
